import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useContext
} from 'react';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ReplayIcon from '@mui/icons-material/Replay';
import style from './style';
import { OrganizationChart } from 'primereact/organizationchart';
import EAIcon from '../../EAIcon';
import EAButton from '../../EAButton';
import i18next from 'i18next';
import NodeTemplate from './NodeTemplate';
import { Context } from '../../../../states/Store';
import { getContextInstanceThirdPartyXls } from '../../../../services/edge/ContexteService';
import moment from 'moment/moment';
import { ERROR } from '../../../../services/common/Constants';
import EASwitch from '../../EASwitch';

const OrgChart = ({
  tree,
  selectedNodeId,
  setIsAddTpDialogOpen,
  associatedTP,
  fetchAssociatedTP,
  fetchContextInstance,
  toggleFullScreen,
  isFullScreen,
  setShowArchived,
  showArchived
}) => {
  const { classes } = style();
  const [state, dispatch] = useContext(Context);

  const [zoomLevel, setZoomLevel] = useState(1);
  const [contentPos, setContentPos] = useState({ x: 0, y: 10 });
  const [isDragging, setIsDragging] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isLoadingXls, setIsLoadingXls] = useState(false);
  const startPos = useRef({ x: 0, y: 0 });

  const containerRef = useRef(null);
  const contentRef = useRef(null);

  const getResetPos = useCallback(() => {
    if (contentRef.current) {
      const tableEl = document.querySelector('.p-organizationchart-table');
      const tableElRectWidth = tableEl?.getBoundingClientRect().width || 0;
      const windowViz = contentRef.current.getBoundingClientRect().width;
      const x = -Math.max(tableElRectWidth, windowViz) / 2 + windowViz / 2;
      setContentPos({ x, y: 100 });
    } else {
      setContentPos({ x: 0, y: 100 });
    }
  }, []);

  useEffect(() => {
    const preventScroll = (e) => {
      if (
        containerRef.current &&
        containerRef.current.contains(e.target) &&
        isFocused
      ) {
        e.preventDefault();
      }
    };
    window.addEventListener('wheel', preventScroll, { passive: false });
    return () => {
      window.removeEventListener('wheel', preventScroll);
    };
  }, [isFocused]);

  useEffect(() => {
    getResetPos();
  }, [getResetPos]);

  const handleWheel = (e) => {
    if (!isFocused) return;
    const rect = containerRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    const mouseXRelativeToContent = (mouseX - contentPos.x) / zoomLevel;
    const mouseYRelativeToContent = (mouseY - contentPos.y) / zoomLevel;

    const zoomChange = e.deltaY > 0 ? -0.13 : 0.13;
    const newZoomLevel = Math.min(Math.max(zoomLevel + zoomChange, 0.1), 2);

    const newContentPosX = mouseX - mouseXRelativeToContent * newZoomLevel;
    const newContentPosY = mouseY - mouseYRelativeToContent * newZoomLevel;

    setZoomLevel(newZoomLevel);
    setContentPos({ x: newContentPosX, y: newContentPosY });
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
    startPos.current = {
      x: e.clientX - contentPos.x,
      y: e.clientY - contentPos.y,
    };
    if (containerRef.current) {
      containerRef.current.style.cursor = 'grabbing';
    }
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging) return;
      setContentPos({
        x: e.clientX - startPos.current.x,
        y: e.clientY - startPos.current.y,
      });
    };
    const handleMouseUp = () => {
      setIsDragging(false);
      if (containerRef.current) {
        containerRef.current.style.cursor = 'grab';
      }
    };
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  useEffect(() => {
    return () => {
      dispatch({
        type: 'SELECTED_CONTEXT_THIRDPARTY',
        contextThirdParty: null,
      });
    };
  }, [dispatch]);

  const addTp = useCallback(
    (selectedNodeId) => {
      setIsAddTpDialogOpen(true, selectedNodeId.identifiant);
    },
    [setIsAddTpDialogOpen]
  );

  const [currentSelectedNodeId, setCurrentSelectedNodeId] = useState(
    selectedNodeId
  );

  const nodeTemplate = useCallback(
    (node) => (
      <NodeTemplate
        node={node}
        currentSelectedNodeId={currentSelectedNodeId}
        addTp={addTp}
        thirdParty={associatedTP.find((tp) => tp.id === node.identifiant)}
        fetchAssociatedTP={fetchAssociatedTP}
        fetchContextInstance={fetchContextInstance}
        isDragging={isDragging}
      />
    ),
    [
      currentSelectedNodeId,
      associatedTP,
      addTp,
      fetchAssociatedTP,
      fetchContextInstance,
      isDragging,
    ]
  );

  const downloadAttributes = useCallback(async () => {
    try {
      setIsLoadingXls(true)
      const resp = await getContextInstanceThirdPartyXls(
        state.account.id,
        associatedTP[0].ctxInstanceId,
        dispatch
      );
      let blob = new Blob([resp?.arrayBuffer]);
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      let dateStr = moment().format('YYYYMMDD');
      link.download = `${dateStr}_${state.account.name}_TIERS_ASSOCIES.xlsx`;
      link.click();
      link.remove();
      setIsLoadingXls(false);
    } catch (e) {
      setIsLoadingXls(false);
      if (!e?.message.messages?.error) {
        console.error(e);
        dispatch({
          type: 'ALERT',
          alert: { type: ERROR, msg: 'INTERNAL_ERROR' },
        });
      }
    }
  }, [state.account, associatedTP, dispatch]);

  const togglerIcon = (node) => <></>;

  const toggleDisplayArchived = (e) => {
    setShowArchived(e.target.checked)
  }

  const containerClass = isFullScreen
    ? `${classes.treeContainer} ${classes.fullScreen}`
    : classes.treeContainer;

  return (
    <div
      ref={containerRef}
      className={containerClass}
      style={{ width: '100%', height: '100%' }}
      onWheel={handleWheel}
      tabIndex={0}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
    >
      <div className={classes.toolBar}>
        <EASwitch
          handleChange={toggleDisplayArchived}
          label={i18next.t("context.showArchived")}
          style={{mr: 'auto'}}
          checked={showArchived}
        />
        {!isFullScreen && (
          <EAButton
            content={
              <>
                <EAIcon icon={'context/contextTree/fullscreen'} />
                <span style={{ marginLeft: '5px' }}>
                  {i18next.t('context.addTiers.fullscreen')}
                </span>
              </>
            }
            customStyle={{
              fontSize: '12px',
            }}
            colorVariant={'secondary'}
            size={'xs'}
            onClick={() => toggleFullScreen()}
          />
        )}
        {associatedTP && associatedTP.length ? (
          <EAButton
            content={
              <>
                <EAIcon icon={'context/download'} />
                <span style={{ marginLeft: '5px' }}>
                  {i18next.t('tableLabels.toolbar.export')}
                </span>
              </>
            }
            customStyle={{
              fontSize: '12px',
            }}
            borderless={true}
            size={'xs'}
            onClick={() => downloadAttributes()}
            disabled={isLoadingXls}
          />
        ):<></>}
      </div>
      <div className={classes.zoomPanel}>
        <AddIcon
          onClick={() => setZoomLevel((prev) => Math.min(prev + 0.1, 2))}
        />
        <RemoveIcon
          onClick={() => setZoomLevel((prev) => Math.max(prev - 0.1, 0.05))}
        />
        <ReplayIcon
          onClick={() => {
            setZoomLevel(1);
            setTimeout(getResetPos, 200);
          }}
        />
        {/*
      <div className={classes.legendContainer}>
        <div className={classes.legendTitle}>
          {i18next.t('context.contextDetail.visualisationLegend.declaredBy')}:
        </div>
        <div className={classes.legend}>
          <div className={classes.legendSquareOrange}></div>
          <span>
            {i18next.t('context.contextDetail.visualisationLegend.tiers')}
          </span>
        </div>
        <div className={classes.legend}>
          <div className={classes.legendSquareBlue}></div>
          <span>
            {i18next.t('context.contextDetail.visualisationLegend.clients')}
          </span>
        </div>
        <div className={classes.legend}>
          <div className={classes.legendSquareViolet}></div>
          <span>
            {i18next.t(
              'context.contextDetail.visualisationLegend.tiersClients'
            )}
          </span>
        </div>
      </div>
*/}
      </div>
      <div
        ref={contentRef}
        style={{
          transform: `translate(${contentPos.x}px, ${contentPos.y}px) scale(${zoomLevel})`,
          position: 'absolute',
          left: 0,
          top: 0,
          width: '100%',
          height: '100%',
          transformOrigin: '0 0',
          /*
                    transition: isDragging ? 'transform 0.1s' : 'none',
          */
          cursor: isDragging ? 'grabbing' : 'grab',
        }}
        onMouseDown={handleMouseDown}
      >
        <OrganizationChart
          value={tree}
          nodeTemplate={nodeTemplate}
          togglerIcon={togglerIcon}
          selection={selectedNodeId}
          selectionMode={'single'}
          onSelectionChange={(e) =>
            setCurrentSelectedNodeId(e.data.identifiant)
          }
          pt={{
            node: {
              style: {
                background: 'transparent',
                width: 'fit-content',
                padding: 0,
                border: 'none',
              },
            },
            nodeToggler: {
              style: {
                background: 'transparent',
                pointerEvents: 'none',
              },
            },
            nodeTogglerIcon: {
              style: {
                pointerEvents: 'all',
              },
            },
            lineLeft: {
              style: {
                borderColor: '#32614F',
              },
            },
            lineRight: {
              style: {
                borderColor: '#32614F',
              },
            },
            lineDown: {
              style: {
                background: '#32614F',
              },
            },
          }}
        />
      </div>
    </div>
  );
};

export default OrgChart;
